import React from "react";
import _ from "lodash";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { useTable, useBlockLayout, useResizeColumns } from "react-table";
import { getLocalDateStringFromUTC, getDateString } from "../common/LocalTimeStamp";
import EncounterDetails from "../routes/encounter/EncounterDetails";

export default function EncounterList({
                                          encounters,
                                          title,
                                          onEncounterSelect,
                                          selectedEncounter,
                                          includeEncounterDetailsModal
                                      }) {
    const isSelectedEncounter = (e) => {
        return !_.isNil(selectedEncounter) && e.id === selectedEncounter.id;
    };

    const defaultColumn = React.useMemo(
        () => ({
            minWidth: 30,
            width: 150,
            maxWidth: 400
        }),
        []
    );

    const columns = React.useMemo(
        () => [
            { Header: "Summary", accessor: "summary" },
            { Header: "Caller Number", accessor: "contact.phone" },
            { Header: "Source", accessor: "source.name" },
            { Header: "Location", accessor: "encounterLocation.summary" },
            {
                id: "unit",
                Header: "Unit",
                accessor: (row) => _.join(_.map(row.units, (u) => u.unitId))
            },
            {
                id: "date",
                Header: "Date/Time",
                accessor: (row) =>
                    row.source?.utc
                        ? getLocalDateStringFromUTC(row.encounterDate, "MM/DD/YYYY, h:mm:ss a")
                        : getDateString(row.encounterDate, "MM/DD/YYYY, h:mm:ss a")
            },
            { Header: "Status", accessor: "status" },
            { Header: "Priority", accessor: "priority" },
            { Header: "Incident Number", accessor: "incidentNumber" },
            { Header: "ETA", accessor: "eta" }
        ],
        []
    );

    const data = React.useMemo(
        () => _.reverse(_.sortBy(encounters, (e) => e.createdAt)),
        [encounters.length]
    );

    const tableInstance = useTable({ columns, data, defaultColumn }, useBlockLayout, useResizeColumns);

    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = tableInstance;

    // Create refs for CSSTransition React 19 compatibility
    const nodeRefs = React.useRef({});

    return (
        <div className="allEncountersList">
            <div className="encountersTitle">{title}</div>

            <div className="detailedEncountersTable" {...getTableProps()}>
                {headerGroups.map((headerGroup) => {
                    const { key: headerGroupKey, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
                    return (
                        <div key={headerGroupKey} className="encountersTableHeader" {...headerGroupProps}>
                            {headerGroup.headers.map((column) => {
                                const { key: headerKey, ...headerProps } = column.getHeaderProps();
                                return (
                                    <div key={headerKey} {...headerProps} className="encountersCell headerCell encountersColumn">
                                        <div className="title">{column.render("Header")}</div>
                                        <div
                                            {...column.getResizerProps()}
                                            className={`resizer ${column.isResizing ? "isResizing" : ""}`}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}

                <div className="encountersTableCells" {...getTableBodyProps()}>
                    <TransitionGroup className="encounter-list">
                        {rows.map((row) => {
                            prepareRow(row);
                            const encounter = row.original;

                            // Create or retrieve ref for this item (React 19 compatibility)
                            if (!nodeRefs.current[encounter.id]) {
                                nodeRefs.current[encounter.id] = React.createRef();
                            }
                            const nodeRef = nodeRefs.current[encounter.id];

                            const rowContent = (
                                <div
                                    className={`encountersTableCell ${isSelectedEncounter(encounter) ? "selected" : ""}`}
                                    onClick={onEncounterSelect ? () => onEncounterSelect(encounter) : null}
                                >
                                    {row.cells.map((cell, idx) => {
                                        const { key, ...cellProps } = cell.getCellProps();
                                        return (
                                            <div key={key} {...cellProps} className="encountersCell rowCell encountersColumn">
                                                <div className="title">
                                                    {encounter.testEncounter && idx === 0 && (
                                                        <div className="testIndicator">
                                                            <div className="indicatorTitle">TEST</div>
                                                        </div>
                                                    )}
                                                    {cell.render("Cell")}
                                                </div>
                                            </div>
                                        );
                                    })}
                                    <div className="separator" />
                                </div>
                            );

                            return (
                                <CSSTransition
                                    key={encounter.id}
                                    timeout={500}
                                    classNames="encounter"
                                    nodeRef={nodeRef}
                                >
                                    <div ref={nodeRef}>
                                        {includeEncounterDetailsModal ? (
                                            <EncounterDetails encounterId={encounter.id}>
                                                {rowContent}
                                            </EncounterDetails>
                                        ) : (
                                            rowContent
                                        )}
                                    </div>
                                </CSSTransition>
                            );
                        })}
                    </TransitionGroup>
                </div>
            </div>
        </div>
    );
}
